import { t, useUiLocale } from '@/i18n/locale';
import { useContext, useEffect, useState } from 'react';
import { CompanionContext } from './CompanionContext';
import { Link } from 'react-router-dom';
import { HelpCircle } from 'lucide-react';

/** Discovery is device-local and child-scoped; the saved buddy profile lives in the API. */
export function BuddyEntryGuide({ kidId }: { kidId: string }) {
  useUiLocale();
  const session = useContext(CompanionContext);
  const canOffer = !session || !!session.snapshot?.profile;
  const [open, setOpen] = useState(false);
  const key = `airbotix:buddy-entry:v1:${kidId}`;
  useEffect(() => {
    const offer = () => {
      try {
        setOpen(
          canOffer &&
            !!localStorage.getItem('airbotix.learn.welcomed') &&
            !localStorage.getItem(key),
        );
      } catch {
        setOpen(false);
      }
    };
    offer();
    window.addEventListener('airbotix:learn-welcome-dismissed', offer);
    return () => window.removeEventListener('airbotix:learn-welcome-dismissed', offer);
  }, [key, canOffer]);
  const dismiss = () => {
    try {
      localStorage.setItem(key, 'seen');
    } catch {
      /* Storage can be unavailable. */
    }
    setOpen(false);
  };
  useEffect(() => {
    if (!open) return;
    const escape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') dismiss();
    };
    window.addEventListener('keydown', escape);
    return () => window.removeEventListener('keydown', escape);
  });
  return (
    <div className="relative flex items-center gap-1">
      <Link to="/learn/companion" className="btn-pill-ghost !px-2 sm:!px-5" onClick={dismiss}>
        {' '}
        {t('My buddy')}{' '}
      </Link>
      <button
        type="button"
        className="grid h-8 w-8 place-items-center rounded-full text-slate2 hover:bg-wash-sky focus-visible:ring-2 focus-visible:ring-brand-coral"
        aria-label={t('How to open my buddy')}
        aria-expanded={open}
        aria-controls="buddy-entry-guide"
        onClick={() => setOpen(!open)}
      >
        <HelpCircle size={18} aria-hidden="true" />
      </button>
      {open && (
        <section
          id="buddy-entry-guide"
          aria-label={t('Find your buddy')}
          className="fixed inset-x-3 top-24 z-30 mt-3 w-auto sm:absolute sm:inset-x-auto sm:right-0 sm:top-full sm:w-72 rounded-3xl border border-hairline bg-canvas p-5 shadow-floating-chrome"
        >
          <p className="eyebrow">{t('Your own little friend')}</p>
          <h2 className="mt-2 text-lg font-bold text-ink">{t('Meet your buddy')}</h2>
          <p className="mt-2 text-sm leading-relaxed text-ink-soft">
            {' '}
            {t('Tap')} <strong>{t('My buddy')}</strong>{' '}
            {t(
              'up here to choose a friend, give them a name, and dress them up. Come back here whenever you want to visit.',
            )}{' '}
          </p>
          <div className="mt-4 flex flex-wrap gap-2">
            <Link className="btn-pill-primary" to="/learn/companion" onClick={dismiss}>
              {' '}
              {t('Let’s go →')}{' '}
            </Link>
            <button type="button" className="btn-pill-ghost" onClick={dismiss}>
              {' '}
              {t('Later')}{' '}
            </button>
          </div>
        </section>
      )}
    </div>
  );
}
